/**
 * TechnologyChart Component
 * 
 * Technology-specific chart component with custom configurations
 * and date range support.
 */

import React from 'react';
import { TrendChart } from './trend-chart';
import { EnhancedTechnologyChart } from './enhanced-technology-chart';
import { TechnologyType, TimeSeries, TrendChartData } from '../../../services/equipment/types';
import { 
  getTechnologyChartConfig, 
  getResponsiveChartDimensions,
  isChartDataEmpty 
} from './chart-utils';
import { trendChartDataList } from '../../../services/equipment/equipment-mock-data';
import Heading from '@amzn/meridian/heading';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';

export interface CustomColors {
  /** Color for nominal/primary line */
  nominal?: string;
  /** Color for baseline/secondary line */
  baseline?: string;
  /** Color for temperature line (thermal charts) */
  temperature?: string;
}

export interface PulseCustomColors {
  /** Color for dB measurements line */
  dB?: string;
  /** Color for Baseline line */
  baseline?: string;
  /** Color for Lubrication (Baseline+8) line */
  lubrication?: string;
  /** Color for Start of Failure (Baseline+16) line */
  startOfFailure?: string;
  /** Color for Critical Failure (Baseline+32) line */
  criticalFailure?: string;
}

export interface TechnologyChartProps {
  /** Technology type for the chart */
  technology: TechnologyType;
  /** Chart data */
  data: TrendChartData | null;
  /** Container width for responsive sizing */
  containerWidth?: number;
  /** Loading state */
  isLoading?: boolean;
  /** Error state */
  error?: string | null;
  /** Custom title override */
  title?: string;
  /** Optional click handler for data points */
  onDataPointClick?: (datum: any) => void;
  /** Whether to enable data point clicks */
  enableDataPointClicks?: boolean;
  /** Size of scatter plot dots in pixels */
  dotSize?: number;
  /** Whether to enable zoom functionality (default: true) */
  enableZoom?: boolean;
  /** Maximum data points to render for performance (default: 1000) */
  maxDataPoints?: number;
  /** Minimum zoom level (default: 1/1000) */
  minimumZoom?: number;
  /** Custom colors for chart lines */
  customColors?: CustomColors;
  /** Pulse-specific custom colors (only applies to Pulse charts) */
  pulseColors?: PulseCustomColors;
}

/**
 * Empty state component for when no data is available
 */
const ChartEmptyState: React.FC<{ technology: TechnologyType }> = ({ technology }) => {
  const config = getTechnologyChartConfig(technology);
  
  return (
    <Column alignmentHorizontal="center" spacing="200">
      <Text type="h400">{config.displayName}</Text>
      <Text>No measurement data available for the selected date range.</Text>
    </Column>
  );
};

/**
 * Loading state component
 */
const ChartLoadingState: React.FC<{ technology: TechnologyType }> = ({ technology }) => {
  const config = getTechnologyChartConfig(technology);
  
  return (
    <Column alignmentHorizontal="center" spacing="200">
      <Text type="h400">{config.displayName}</Text>
      <Text>Loading measurement data...</Text>
    </Column>
  );
};

/**
 * Error state component
 */
const ChartErrorState: React.FC<{ technology: TechnologyType; error: string }> = ({ 
  technology, 
  error 
}) => {
  const config = getTechnologyChartConfig(technology);
  
  return (
    <Column spacing="200">
      <Text type="h400">{config.displayName}</Text>
      <Alert type="error" size="small">
        Failed to load {config.displayName.toLowerCase()} data: {error}
      </Alert>
    </Column>
  );
};

/**
 * TechnologyChart component with technology-specific configurations
 */
export const TechnologyChart: React.FC<TechnologyChartProps> = ({
  technology,
  data,
  containerWidth = 600,
  isLoading = false,
  error = null,
  title,
  onDataPointClick,
  enableDataPointClicks = false,
  dotSize,
  enableZoom = true,
  maxDataPoints = 1000,
  minimumZoom = 0.1,
  customColors,
  pulseColors,
}) => {
  const config = getTechnologyChartConfig(technology);
  const dimensions = getResponsiveChartDimensions(containerWidth);

  // Handle loading state
  if (isLoading) {
    return <ChartLoadingState technology={technology} />;
  }

  // Handle error state
  if (error) {
    return <ChartErrorState technology={technology} error={error} />;
  }

  // Handle empty data
  if (!data || isChartDataEmpty(data)) {
    return <ChartEmptyState technology={technology} />;
  }

  // Render the chart with technology-specific configuration
  return (
    <TrendChart
      data={data}
      width={dimensions.width}
      height={dimensions.height}
      showIndependentGrid={config.showIndependentGrid}
      showCrosshair={config.showCrosshair}
      title={title || config.displayName}
      onDataPointClick={onDataPointClick}
      enableDataPointClicks={enableDataPointClicks}
      dotSize={dotSize}
      enableZoom={enableZoom}
      maxDataPoints={maxDataPoints}
      minimumZoom={minimumZoom}
      customColors={customColors}
      pulseColors={pulseColors}
    />
  );
};

/**
 * Monitron-specific chart component using enhanced chart with dual Y-axes
 */
export const MonitronChart: React.FC<Omit<TechnologyChartProps, 'technology'> & {
  site?: string;
  equipmentId?: string;
  position?: string;
}> = (props) => {
  // Use the enhanced technology chart for better visualization
  return (
    <EnhancedTechnologyChart
      technology="monitron"
      data={props.data}
      containerWidth={props.containerWidth}
      isLoading={props.isLoading}
      error={props.error}
      height={400}
      site={props.site}
      equipmentId={props.equipmentId}
      position={props.position}
    />
  );
};

/**
 * Pulse-specific chart component
 */
export const PulseChart: React.FC<Omit<TechnologyChartProps, 'technology'>> = (props) => (
  <TechnologyChart {...props} technology="pulse" />
);

/**
 * Cadet-specific chart component using enhanced chart
 */
export const CadetChart: React.FC<Omit<TechnologyChartProps, 'technology'>> = (props) => {
  return (
    <EnhancedTechnologyChart
      technology="cadet"
      data={props.data}
      containerWidth={props.containerWidth}
      isLoading={props.isLoading}
      error={props.error}
      height={400}
    />
  );
};

/**
 * Thermal-specific chart component
 */
export const ThermalChart: React.FC<Omit<TechnologyChartProps, 'technology'>> = (props) => (
  <TechnologyChart {...props} technology="thermal" dotSize={4} />
);
